import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Badge } from '@bnvsrnd/brand-assets';
import api from '../../utils/api';
import { useNotifications } from '../../contexts/NotificationContext';

/**
 * 슈퍼바이저 공지·메시지 수신 모달(강사 포털 전체에 한 번 깔린다 — InstructorLayout).
 * 포털을 열 때 안 읽은 것을 불러오고, 접속 중에 새로 오면 소켓('instructorNotice:new')으로 바로 띄운다.
 * '확인'을 눌러야 닫히며(바깥 클릭·ESC 로는 안 닫힘) 그때 읽음으로 기록돼 보낸 사람 화면에 반영된다.
 */
const formatTime = (v) => (v ? new Date(v).toLocaleString('ko-KR', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');

const InstructorNoticeModal = () => {
    const { socket } = useNotifications();
    const [queue, setQueue] = useState([]);
    const [confirming, setConfirming] = useState(false);

    const enqueue = useCallback((items) => {
        setQueue((prev) => {
            const seen = new Set(prev.map((n) => n._id));
            return [...prev, ...items.filter((n) => !seen.has(n._id))];
        });
    }, []);

    useEffect(() => {
        api.get('/supervision/inbox')
            .then((res) => enqueue(res.data.data || []))
            .catch(() => { /* 받은 공지 조회 실패는 화면 사용을 막지 않는다 */ });
    }, [enqueue]);

    useEffect(() => {
        if (!socket) return undefined;
        const onNew = ({ notice }) => notice && enqueue([notice]);
        socket.on('instructorNotice:new', onNew);
        return () => socket.off('instructorNotice:new', onNew);
    }, [socket, enqueue]);

    const current = queue[0];

    const handleConfirm = async () => {
        if (!current) return;
        setConfirming(true);
        try {
            await api.post(`/supervision/inbox/${current._id}/read`);
        } catch {
            /* 확인 기록이 실패해도 모달은 닫는다 — 다음 접속 때 다시 뜬다 */
        } finally {
            setConfirming(false);
            setQueue((prev) => prev.slice(1));
        }
    };

    if (!current) return null;
    const isAnnouncement = current.kind === 'announcement';

    return (
        <Modal open onClose={() => {}} closeOnBackdrop={false} closeOnEscape={false} size="md">
            <Modal.Header showClose={false}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <Badge variant="soft" color={isAnnouncement ? 'primary' : 'neutral'} size="sm">
                        {isAnnouncement ? '공지' : '메시지'}
                    </Badge>
                    {current.title || (isAnnouncement ? '슈퍼바이저 공지' : `${current.senderName}님의 메시지`)}
                </span>
            </Modal.Header>
            <Modal.Body>
                <div style={{ font: 'var(--body-default)', color: 'var(--text-primary)', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{current.body}</div>
                <div style={{ font: 'var(--body-small)', color: 'var(--text-tertiary)', marginTop: 'var(--space-4)' }}>
                    {current.senderName} · {formatTime(current.createdAt)}
                    {queue.length > 1 && ` · 남은 알림 ${queue.length - 1}개`}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="primary" onClick={handleConfirm} disabled={confirming}>확인</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default InstructorNoticeModal;
